import type { AddressI } from "../../pojo/AddressI";
import type { Task } from "../../pojo/Task";
import type { Proto } from "../../pojo/Proto";
import { S25ItemI } from "../../pojo/S25ItemI";
import { UserprefService } from "../../services/userpref.service";
import { ContactService } from "../../services/contact.service";
import { getTodoVars } from "./bpe.util";
import type { BpeService } from "./bpe.service";
import type { Event } from "../../pojo/Event";
type StateName = Task.StateName;
type CharBoolean = Proto.CharBoolean;
type NumericalString = Proto.NumericalString;

export namespace Bpe {
    export type BpeScenarioTypes =
        | "event-form"
        | "express"
        | "event-state-change"
        | "task"
        | "cancelTodos"
        | "createTodo"
        | "occurrence-inline-edit"
        | "event-details-inline-edit";

    export type BpeVarsCombined = {
        taskActions: Tasks.ActionsMap;
    } & BpeVars &
        Partial<BpePreVars>;

    type MutualVars = {
        timeFormat: string;
        dateFormat: string;
        dateTimeFormat: string;
        source: any; //source,
        eventId: number;
        eventUrl: string;
        eventLink: string; //htmlString
        homeUrl: string; //url
        eventLocator: string;
        eventName: string;
        eventTitle: string;
        versionNumber: number;
        eventTypeId: number;
        eventTypeName: string;
        description: string; //this is rich text
        internalNote: string;
        confirmationNote: string;
        comment: string;
        customAttributes: any;
        customAttributesList: any[];
        categories: {
            id: number;
            isActive: "Yes" | "No";
            name: string;
        }[];
        billItems: any; //billItemList,  relationships,  tasks,  contacts,  segments,  organizationsList,
        roleContactId: number;
        roleContactName: string;
        roleContactEmail: string;
        roleContactAddr: AddressI;
        roleContactFirst: string;
        roleContactLast: string;
        roleContactMiddle: string;
        roleContactPhone: string;
        roleContactFax: string;
        state: number;
        stateName: string;
        requesterEmail: string;
        requestorEmail: string; //alternate spelling
        schedulerEmail: string;
        primaryOrganization: any; //primaryOrganization,
        requirements: any; //requirements,
        organizations: any; //orgVars,
        dates: {
            lastModDate: Date;
            creationDate: Date;
            currentLocalDate: Date;
            //reservation start and end dates from earliest/ latest reservations
            startDate: Date;
            endDate: Date;
            eventStartDate: Date;
            eventEndDate: Date;
            eventSetupDate: Date;
            eventTakedownDate: Date;
            preEventDate: Date;
            postEventDate: Date;
        };
        pricing: {
            grandTotal: string;
        };
        durations: {
            setup: string; //duration
            takeDown: string; //duration,
            preEvent: string; //duration,
            postEvent: string; //duration,
            event: string; //duration,
            preToPost: string; //duration,
            setupToTakedown: string; //duration,
        };

        //occurrence info:
        occurrences: ScenarioOccurrence[]; //globalOccurrences,
        occurrencesResources: any[];
        occurrencesLocations: any[];
        occurrencesLocationsResources: any[]; //occurrencesLocations.concat(occurrencesResources).sort((a, b) => a.dates.rsrvStartDate - b.dates.rsrvStartDate),
        locations: any; //globalLocations,
        resources: any; //globalResources,

        //pref occurrence info:
        prefOccurrences: any; //globalPrefOccurrences,
        prefOccurrencesResources: any;
        prefOccurrencesLocations: any;
        prefOccurrencesLocationsResources: any; // prefOccurrencesLocations.concat(prefOccurrencesResources.sort((a, b) => a.dates.rsrvStartDate - b.dates.rsrvStartDate),
        prefLocations: any; // globalPrefLocations,
        prefResources: any; // globalPrefResources,
        tasks: Task[];

        //todo info:
        todo: any[];
    };

    export type BpeVars = MutualVars & {
        rebuildWith: (additionalData: GetVariablesData["additionalData"]) => BpeVars;
        actionType: "create" | "edit";
        expectedCount: NumericalString;
        registeredCount: NumericalString;
        maxExpectedCount: number;

        //currentUser
        currentGroupId: number;
        currentContactName: string;

        scenario: any; // is this needed??
        template: any;

        //functions
        testTaskTriggers: Function;
        testAttributes: Function;
        testHeadcount: Function;
    };
    export type BpePreVars = { [K in keyof MutualVars as `pre${Capitalize<K>}`]: MutualVars[K] } & {};

    export type Task = {
        comment: string;
        id: number; // task_id
        name: string; //Object name,
        state: StateName;
        stateId: Task.State;
        type: Task.IdName;
        flagged: CharBoolean;
        assignedTo: string; //username
        assignedToIds: number[];
        object: "event type" | "space" | "resource" | "organization" | "requirement";
        respondByDate: Date;
        firstDate: Date;
        item: S25ItemI;
    };

    export namespace Tasks {
        export type Action = Task.Actions | Actions;
        export enum Actions {
            create = "create",
            delete = "delete",
            none = "none",
        }

        export type ActionsMap = {
            [key in Action]: Bpe.Task[];
        };
    }

    export type GetVariablesData = {
        eventData: any;
        profileId: any;
        preEventData: any;
        source?: BpeScenarioTypes;
        additionalData?: {
            locationDetails: Awaited<ReturnType<typeof BpeService.getConditionalLocations>>;
        };
    };

    export type GetVariablesAsyncData = {
        taskPrep: Awaited<ReturnType<typeof getTodoVars>>;
        currentGroupId: Awaited<ReturnType<typeof UserprefService.getGroupId>>;
        currentContactName: Awaited<ReturnType<typeof ContactService.getCurrentNameStyled>>;
        currentContactId: Awaited<ReturnType<typeof UserprefService.getContactId>>;
        timeFormat: Awaited<ReturnType<typeof UserprefService.getS25Timeformat>>;
        dateFormat: Awaited<ReturnType<typeof UserprefService.getS25Dateformat>>;
        dateTimeFormat: Awaited<ReturnType<typeof UserprefService.getS25DateTimeformat>>;
    };

    type ScenarioOccurrence = {
        dates: ScenarioOccurrenceDates;
        rsrvState: Event.Reservation.StateId;
    };

    type ScenarioOccurrenceDates = {
        rsrvStartDate: Date;
        rsrvPreEventDate: Date;
        rsrvEventStartDate: Date;
        rsrvEventEndDate: Date;
        rsrvPostEventDate: Date;
        rsrvEndDate: Date;
    };
}
